import { useState, useEffect } from 'react';
import { executionApi } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  ChevronLeft,
  Clock,
  User,
  Workflow,
  AlertCircle,
  CheckCircle,
  XCircle,
  Terminal,
  Eye,
} from 'lucide-react';
import { cn } from '../utils/cn';

interface Execution {
  id: string;
  workflowId: string;
  workflowName: string;
  userId: string;
  username: string;
  status: 'success' | 'failed' | 'running' | 'timeout';
  startedAt: string;
  endedAt?: string;
  duration: number;
  errorMessage?: string;
}

interface ExecutionDetail {
  id: string;
  workflowId: string;
  workflowName: string;
  userId: string;
  username: string;
  status: string;
  startedAt: string;
  endedAt?: string;
  duration: number;
  errorMessage?: string;
  nodes: Array<{
    id: string;
    nodeId: string;
    nodeType: string;
    nodeLabel: string;
    status: string;
    startedAt?: string;
    endedAt?: string;
    duration: number;
    errorMessage?: string;
  }>;
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  errorStack?: string;
}

export function ExecutionsPage() {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<ExecutionDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchExecutions = async () => {
    setIsLoading(true);
    try {
      const response = await executionApi.listExecutions({
        page,
        pageSize,
        status: status || undefined,
      });
      if (response.code === 0) {
        setExecutions(response.data.items as Execution[]);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch executions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExecutions();
  }, [page, pageSize, status]);

  const fetchExecutionDetail = async (executionId: string) => {
    try {
      const response = await executionApi.getExecutionDetail(executionId);
      if (response.code === 0) {
        setSelectedExecution(response.data);
        setShowDetail(true);
      }
    } catch (err) {
      console.error('Failed to fetch execution detail:', err);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('zh-CN');
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="success">成功</Badge>;
      case 'failed':
        return <Badge variant="error">失败</Badge>;
      case 'running':
        return <Badge variant="info">运行中</Badge>;
      case 'timeout':
        return <Badge variant="warning">超时</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-error-600" />;
      case 'running':
        return <Clock className="w-5 h-5 text-accent-600 animate-pulse" />;
      case 'timeout':
        return <AlertCircle className="w-5 h-5 text-warning-600" />;
      default:
        return <Clock className="w-5 h-5 text-zinc-400" />;
    }
  };

  if (showDetail && selectedExecution) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => setShowDetail(false)}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900"
        >
          <ChevronLeft className="w-4 h-4" />
          返回运行记录
        </button>

        {/* Execution Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            {getStatusIcon(selectedExecution.status)}
            <h1 className="text-2xl font-bold text-zinc-900">运行详情</h1>
            <Badge variant={selectedExecution.status === 'success' ? 'success' : selectedExecution.status === 'failed' ? 'error' : 'warning'}>
              {selectedExecution.status}
            </Badge>
          </div>
          <p className="text-zinc-500 font-mono">ID: {selectedExecution.id}</p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Workflow className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">工作流</p>
                  <p className="font-medium text-zinc-900">{selectedExecution.workflowName}</p>
                  <p className="text-xs text-zinc-400 font-mono">{selectedExecution.workflowId.slice(0, 8)}...</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">执行用户</p>
                  <p className="font-medium text-zinc-900">{selectedExecution.username}</p>
                  <p className="text-xs text-zinc-400 font-mono">{selectedExecution.userId.slice(0, 8)}...</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">执行时间</p>
                  <p className="font-medium text-zinc-900">{formatDuration(selectedExecution.duration)}</p>
                  <p className="text-xs text-zinc-400">{formatDate(selectedExecution.startedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Node Execution Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>节点执行过程</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {selectedExecution.nodes.map((node, index) => (
                <div key={node.id} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      node.status === 'success' ? 'bg-success-100' :
                      node.status === 'failed' ? 'bg-error-100' :
                      node.status === 'running' ? 'bg-accent-100' : 'bg-zinc-100'
                    )}>
                      {getStatusIcon(node.status)}
                    </div>
                    {index < selectedExecution.nodes.length - 1 && (
                      <div className="w-0.5 h-full min-h-[40px] bg-zinc-200 my-1"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <div className={cn(
                      "rounded-lg p-4 border",
                      node.status === 'success' ? 'bg-success-50 border-success-100' :
                      node.status === 'failed' ? 'bg-error-50 border-error-100' :
                      node.status === 'running' ? 'bg-accent-50 border-accent-100' : 'bg-zinc-50 border-zinc-200'
                    )}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-zinc-900">{node.nodeLabel}</p>
                          <p className="text-sm text-zinc-500">类型: {node.nodeType}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant={node.status === 'success' ? 'success' : node.status === 'failed' ? 'error' : 'default'}>
                            {node.status}
                          </Badge>
                          <p className="text-xs text-zinc-500 mt-1">{formatDuration(node.duration)}</p>
                        </div>
                      </div>
                      {node.errorMessage && (
                        <div className="mt-2 p-2 bg-error-100 rounded text-error-700 text-sm">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          {node.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Input/Output Data */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>输入数据</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedExecution.inputData ? (
                <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-4 overflow-x-auto text-sm">
                  {JSON.stringify(selectedExecution.inputData, null, 2)}
                </pre>
              ) : (
                <p className="text-zinc-500 text-center py-8">无输入数据</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>输出数据</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedExecution.outputData ? (
                <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-4 overflow-x-auto text-sm">
                  {JSON.stringify(selectedExecution.outputData, null, 2)}
                </pre>
              ) : (
                <p className="text-zinc-500 text-center py-8">无输出数据</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Error Stack */}
        {selectedExecution.errorStack && (
          <Card>
            <CardHeader>
              <CardTitle className="text-error-600 flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                错误堆栈
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-error-50 text-error-700 rounded-lg p-4 overflow-x-auto text-sm font-mono">
                {selectedExecution.errorStack}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">运行记录</h1>
          <p className="text-zinc-500 mt-1">查看所有工作流执行记录</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            >
              <option value="">所有状态</option>
              <option value="success">成功</option>
              <option value="failed">失败</option>
              <option value="running">运行中</option>
              <option value="timeout">超时</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Executions Table */}
      <Card>
        <CardHeader>
          <CardTitle>执行记录</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>状态</TableHead>
                    <TableHead>工作流</TableHead>
                    <TableHead>用户</TableHead>
                    <TableHead>开始时间</TableHead>
                    <TableHead>耗时</TableHead>
                    <TableHead>错误信息</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executions.map((execution) => (
                    <TableRow key={execution.id}>
                      <TableCell>{getStatusBadge(execution.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Workflow className="w-4 h-4 text-zinc-400" />
                          <span className="font-medium text-zinc-900">{execution.workflowName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span>{execution.username}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(execution.startedAt)}</TableCell>
                      <TableCell>{formatDuration(execution.duration)}</TableCell>
                      <TableCell>
                        {execution.errorMessage ? (
                          <span className="text-error-600 text-sm truncate max-w-[200px] block" title={execution.errorMessage}>
                            {execution.errorMessage}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchExecutionDetail(execution.id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          查看
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
